import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import type { DocxEditorProps, DocxEditorRef } from '@eigenpal/docx-editor-react';

/** The editor component, ref-forwarding so callers can drive it (setZoom, save, …). */
export type DocxEditorComponent = ForwardRefExoticComponent<
  DocxEditorProps & RefAttributes<DocxEditorRef>
>;

/**
 * Shared, memoized loader for the docx-editor.dev editor bundle.
 *
 * The editor is heavy (~1.8 MB raw / ~0.55 MB gzip, code-split into its own chunks), so it is loaded
 * on demand rather than in the main bundle. Both the on-demand mount (DocxEditorPane) and the idle
 * PREFETCH below go through this single promise — so warming it in the background while the user is on
 * another page makes the first document open instant, with no double download.
 */
let editorPromise: Promise<DocxEditorComponent | null> | null = null;

/** Load (or return the in-flight/cached) DocxEditor component. Client-only; null on the server or on failure. */
export function loadDocxEditor(): Promise<DocxEditorComponent | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!editorPromise) {
    editorPromise = (async () => {
      try {
        await import('@eigenpal/docx-editor-react/styles.css');
        const mod = await import('@eigenpal/docx-editor-react');
        return (mod.DocxEditor as unknown as DocxEditorComponent) ?? null;
      } catch {
        editorPromise = null; // clear so a later, real open can retry
        return null;
      }
    })();
  }
  return editorPromise;
}

interface IdleWindow {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
}
interface DataConnection {
  saveData?: boolean;
  effectiveType?: string;
}

/**
 * Warm the editor bundle during browser idle time (e.g. while the user is on the dashboard), so opening a
 * document later doesn't wait on a ~0.5 MB download. No-op on the server, when already loaded/loading, or on
 * a data-saver / very slow connection (we don't spend a user's data on something they may not open).
 */
export function prefetchDocxEditor(): void {
  if (typeof window === 'undefined' || editorPromise) return;

  const conn = (navigator as Navigator & { connection?: DataConnection }).connection;
  if (conn && (conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType ?? ''))) return;

  const warm = () => void loadDocxEditor();
  const ric = (window as Window & IdleWindow).requestIdleCallback;
  if (ric) ric(warm, { timeout: 4000 });
  else window.setTimeout(warm, 1500);
}
